package dev.lingvo.shared.screenshots.scene

// SHA-256 round constants, kept in common code so generated ids are identical on JVM and JS.
private val namespaceRounds = """
    428a2f98 71374491 b5c0fbcf e9b5dba5 3956c25b 59f111f1 923f82a4 ab1c5ed5
    d807aa98 12835b01 243185be 550c7dc3 72be5d74 80deb1fe 9bdc06a7 c19bf174
    e49b69c1 efbe4786 0fc19dc6 240ca1cc 2de92c6f 4a7484aa 5cb0a9dc 76f988da
    983e5152 a831c66d b00327c8 bf597fc7 c6e00bf3 d5a79147 06ca6351 14292967
    27b70a85 2e1b2138 4d2c6dfc 53380d13 650a7354 766a0abb 81c2c92e 92722c85
    a2bfe8a1 a81a664b c24b8b70 c76c51a3 d192e819 d6990624 f40e3585 106aa070
    19a4c116 1e376c08 2748774c 34b0bcb5 391c0cb3 4ed8aa4a 5b9cca4f 682e6ff3
    748f82ee 78a5636f 84c87814 8cc70208 90befffa a4506ceb bef9a3f7 c67178f2
""".trim().split(Regex("\\s+")).map { it.toLong(16).toInt() }

/** Compact ids must not collapse easily constructed String.hashCode collisions across document namespaces. */
internal fun namespaceDigest(value: String): String {
    val bytes = value.encodeToByteArray()
    val padded = ByteArray((bytes.size + 9 + 63) / 64 * 64)
    bytes.copyInto(padded)
    padded[bytes.size] = 0x80.toByte()
    val bits = bytes.size.toLong() * 8
    repeat(8) { padded[padded.lastIndex - it] = (bits ushr (it * 8)).toByte() }
    val state = intArrayOf(0x6a09e667, 0xbb67ae85.toInt(), 0x3c6ef372, 0xa54ff53a.toInt(),
        0x510e527f, 0x9b05688c.toInt(), 0x1f83d9ab, 0x5be0cd19)
    for (start in padded.indices step 64) {
        val words = IntArray(64)
        repeat(16) { i ->
            repeat(4) { j -> words[i] = (words[i] shl 8) or (padded[start + i * 4 + j].toInt() and 255) }
        }
        for (i in 16 until 64) {
            val x = words[i - 15]
            val y = words[i - 2]
            val s0 = x.rotateRight(7) xor x.rotateRight(18) xor (x ushr 3)
            val s1 = y.rotateRight(17) xor y.rotateRight(19) xor (y ushr 10)
            words[i] = words[i - 16] + s0 + words[i - 7] + s1
        }
        var a = state[0]
        var b = state[1]
        var c = state[2]
        var d = state[3]
        var e = state[4]
        var f = state[5]
        var g = state[6]
        var h = state[7]
        repeat(64) { i ->
            val sum1 = e.rotateRight(6) xor e.rotateRight(11) xor e.rotateRight(25)
            val choose = (e and f) xor (e.inv() and g)
            val t1 = h + sum1 + choose + namespaceRounds[i] + words[i]
            val sum0 = a.rotateRight(2) xor a.rotateRight(13) xor a.rotateRight(22)
            val majority = (a and b) xor (a and c) xor (b and c)
            val t2 = sum0 + majority
            h = g
            g = f
            f = e
            e = d + t1
            d = c
            c = b
            b = a
            a = t1 + t2
        }
        val block = intArrayOf(a, b, c, d, e, f, g, h)
        state.indices.forEach { state[it] += block[it] }
    }
    return state.take(4).joinToString("") { it.toUInt().toString(16).padStart(8, '0') }
}
